import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import DataTable from "components/QuestDataTable";
import { API_URL } from "config";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "../../../AuthContext";
import { Card, Icon, Typography } from "@mui/material";
import SoftInput from "components/SoftInput";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
// import SearchableSelect from "components/SearchableSelect";
import SearchableSelectLazy from "components/SearchableSelectLazy";
import SoftPagination from "components/SoftPagination";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { shiftDisplayLabel } from "utils/shiftDisplay";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";

const UsersList = () => {
  const { isAuthReady, permissions = [], userPermission = [], user: authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [pagination, setPagination] = useState({ page: 1, perPage: 25, total: 0 });
  const [pageInput, setPageInput] = useState(1);
  const [searchName, setSearchName] = useState("");
  const [debouncedFilters, setDebouncedFilters] = useState({
    searchName: "",
    formData: {},
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, loading: false });

  // Form data
  const [formData, setFormData] = useState({
    department_id: "",
    designation_id: "",
    role_id: "",
    branch_id: "",
    subbranch_id: "",
    shift_id: null, // single shift id
    manager_id: "",
    team_leader_id: "",
    owner: "",
  });

  // Dropdown data
  const [dropdowns, setDropdowns] = useState({
    department: [],
    designation: [],
    branches: [],
    subBranches: [],
    roles: [],
    branchManagers: [],
    branchShifts: [],
    teamLeaders: [],
  });

  // Loading states
  const [loading, setLoading] = useState({
    initial: true,
    branch: false,
    teamLeaders: false,
  });

  const navigate = useNavigate();
  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );
  const hasUsersPermission = useCallback(
    (...requiredPermissions) => hasPermission(grantedPermissions, ...requiredPermissions),
    [grantedPermissions]
  );
  const canUseOwnerFilter = authUser?.actor_type === "user" && Number(authUser?.vendor_id || 0) <= 0;
  const canViewPage = hasUsersPermission("users.pages.list.view");
  const canCreate =
    hasUsersPermission("users.pages.create.view") &&
    hasUsersPermission("users.actions.open_create");
  const canOpenTrashed =
    hasUsersPermission("users.pages.trashed.view") &&
    hasUsersPermission("users.actions.open_trashed");
  const canOpenEdit =
    hasUsersPermission("users.pages.edit.view") &&
    hasUsersPermission("users.actions.open_edit");
  const canDelete = hasUsersPermission("users.actions.delete");
  const canConfirmDelete = hasUsersPermission("users.actions.confirm_delete");
  const canCancelDelete = hasUsersPermission("users.actions.cancel_delete");
  const canManagePermissionOverrides =
    authUser?.actor_type === "user" && hasUsersPermission("access.users.permissions.manage");
  const currentUserId = String(authUser?.id ?? "");
  const canViewField = useCallback(
    (field) => hasUsersPermission(`users.fields.${field}.view`),
    [hasUsersPermission]
  );


  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("authToken");
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await readJsonSafely(response);
      throw new Error(apiErrorMessage(error, "Request failed"));
    }

    return readJsonSafely(response);
  }, []);

  // Load departments, designations, branches, roles
  // useEffect(() => {
  //   const loadInitialData = async () => {
  //     try {
  //       const [departmentsRes, designationsRes, branchesRes, rolesRes] = await Promise.all([
  //         getDepartments(1, 250),
  //         getDesignations(1, 250),
  //         getBranches(1, 250),
  //         getRoles(1, 250),
  //       ]);

  //       setDropdowns((prev) => ({
  //         ...prev,
  //         department: Array.isArray(departmentsRes?.data)
  //           ? departmentsRes.data.map((d) => ({
  //             label: d.name,
  //             value: d.id,
  //           }))
  //           : [],
  //         designation: Array.isArray(designationsRes?.data)
  //           ? designationsRes.data.map((d) => ({
  //             label: d.name,
  //             value: d.id,
  //           }))
  //           : [],
  //         branches: Array.isArray(branchesRes?.data)
  //           ? branchesRes.data.map((b) => ({
  //             label: b.name,
  //             value: b.id,
  //           }))
  //           : [],
  //         roles: Array.isArray(rolesRes?.roles)
  //           ? rolesRes.roles.map((r) => ({
  //             label: r.name,
  //             value: r.id,
  //           }))
  //           : [],
  //       }));
  //     } catch (error) {
  //       console.error("Error loading initial data:", error);
  //     } finally {
  //       setLoading((prev) => ({ ...prev, initial: false }));
  //     }
  //   };

  //   loadInitialData();
  // }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters({
        searchName,
        formData: { ...formData }, // 👈 CLONE OBJECT (IMPORTANT)
      });
    }, 300); // 300ms delay

    return () => clearTimeout(handler);
  }, [searchName, formData]);

  // Load branch managers + shifts
  // Load branch managers + sub branches
  useEffect(() => {
    const loadBranchData = async () => {
      if (!formData.branch_id) {
        setDropdowns((prev) => ({
          ...prev,
          subBranches: [],
          branchManagers: [],
          branchShifts: [],
          teamLeaders: [],
        }));

        setFormData((prev) => ({
          ...prev,
          subbranch_id: "",
          manager_id: "",
          team_leader_id: "",
          shift_id: null,
        }));
        return;
      }

      setLoading((prev) => ({ ...prev, branch: true }));

      try {
        const [managersRes, subBranchesRes] = await Promise.all([
          canViewField("manager")
            ? fetchWithAuth(`${API_URL}/users/options/managers/${formData.branch_id}`)
            : Promise.resolve({ data: [] }),
          canViewField("sub_branch")
            ? fetchWithAuth(`${API_URL}/users/options/sub-branches/${formData.branch_id}`)
            : Promise.resolve({ data: [] }),
        ]);

        const managers = Array.isArray(managersRes?.data) ? managersRes.data : Array.isArray(managersRes) ? managersRes : [];
        const subBranches = Array.isArray(subBranchesRes?.data) ? subBranchesRes.data : Array.isArray(subBranchesRes?.subbranches) ? subBranchesRes.subbranches : Array.isArray(subBranchesRes) ? subBranchesRes : [];

        setDropdowns((prev) => ({
          ...prev,
          branchManagers: managers.map((m) => ({ label: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim(), value: m.id })),
          subBranches: subBranches.map((sb) => ({ label: sb.name, value: sb.id })),
        }));

        if (formData.subbranch_id) {
           const exists = subBranches.some(sb => sb.id.toString() === formData.subbranch_id);
           if (!exists) setFormData(prev => ({ ...prev, subbranch_id: "", shift_id: null }));
        }

      } catch (error) {
        console.error("Error loading branch data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, branch: false }));
      }
    };
    loadBranchData();
  }, [canViewField, fetchWithAuth, formData.branch_id]);

  useEffect(() => {
    const loadShifts = async () => {
      if (!formData.subbranch_id) {
        setDropdowns((prev) => ({ ...prev, branchShifts: [] }));
        setFormData((prev) => ({ ...prev, shift_id: null }));
        return;
      }
      try {
        const shiftsRes = await fetchWithAuth(`${API_URL}/users/options/shifts/${formData.subbranch_id}`);
        const shifts = Array.isArray(shiftsRes?.data) ? shiftsRes.data : Array.isArray(shiftsRes?.shifts) ? shiftsRes.shifts : Array.isArray(shiftsRes) ? shiftsRes : [];

        setDropdowns((prev) => ({
          ...prev,
          branchShifts: shifts.map((s) => ({
            label: shiftDisplayLabel(s),
            value: s.id,
          })),
        }));
      } catch (error) {
        console.error("Error loading shifts:", error);
      }
    };
    loadShifts();
  }, [fetchWithAuth, formData.subbranch_id]);

  // Load team leaders (branch + shift dependent)
  useEffect(() => {
    const loadTeamLeaders = async () => {
      if (!formData.branch_id || !formData.shift_id) {
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
        setFormData((prev) => ({ ...prev, team_leader_id: "" }));
        return;
      }

      setLoading((prev) => ({ ...prev, teamLeaders: true }));

      try {
        const response = await fetchWithAuth(
          `${API_URL}/users/options/team-leaders?branch_id=${formData.branch_id}&shift_id=${formData.shift_id}`
        );
        const teamLeaders = response?.data || response?.team_leaders || [];

        setDropdowns((prev) => ({
          ...prev,
          teamLeaders: teamLeaders.map((tl) => ({
            label: tl.name || "Unknown",
            value: tl.id,
          })),
        }));
      } catch (error) {
        console.error("Error loading team leaders:", error);
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, teamLeaders: false }));
      }
    };

    loadTeamLeaders();
  }, [formData.branch_id, formData.shift_id]);

  // ---------------- HANDLERS ----------------
  const handleSelectChange = (field) => (option) => {
    const value = option?.value || "";
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "branch_id") {
        next.subbranch_id = "";
        next.shift_id = null;
        next.manager_id = "";
        next.team_leader_id = "";
      } else if (field === "subbranch_id") {
        next.shift_id = null;
      } else if (field === "shift_id") {
        next.team_leader_id = "";
      }
      return next;
    });
  };

  // Selected role helper
  const selectedRole =
    dropdowns?.roles?.find((r) => r.value === formData.role_id)?.label?.toLowerCase() || "";

  const buildUsersQueryString = useCallback(
    (includePagination = true) => {
      const params = new URLSearchParams();
      if (includePagination) {
        params.set("per_page", pagination.perPage);
        params.set("page", pagination.page);
      }
      params.set("q", debouncedFilters.searchName || "");

      const fd = debouncedFilters.formData || {};
      const filterKeys = [
        "role_id",
        "shift_id",
        "branch_id",
        "subbranch_id",
        "department_id",
        "designation_id",
      ];
      if (canUseOwnerFilter) {
        filterKeys.push("owner");
      }

      filterKeys.forEach((key) => {
        if (fd[key] && fd[key] !== "null") {
          params.set(key, fd[key]);
        }
      });

      return params.toString();
    },
    [canUseOwnerFilter, debouncedFilters, pagination.page, pagination.perPage]
  );

  useEffect(() => {
    if (canUseOwnerFilter || !formData.owner) return;
    setFormData((prev) => ({ ...prev, owner: "" }));
  }, [canUseOwnerFilter, formData.owner]);

  const fetchUsers = async () => {
    if (!canViewPage) {
      setLoading((prev) => ({ ...prev, initial: false }));
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token missing");

      const queryString = buildUsersQueryString();

      const res = await fetch(`${API_URL}/users?${queryString}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      let usersData = [],
        totalCount = 0;

      if (data.users?.data) {
        usersData = data.users.data;
        totalCount = data.users.total;
      } else if (Array.isArray(data.data)) {
        usersData = data.data;
        totalCount = data.total;
      }

      setUsers(usersData);
      setPagination((p) => ({ ...p, total: totalCount }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Failed to fetch users", color: "error" });
      setUsers([]);
    }
  };

  // useEffect(() => {
  //   fetchUsers();
  // }, [pagination.page, pagination.perPage, searchName, formData]);

  const debouncedKey = JSON.stringify(debouncedFilters);

  useEffect(() => {
    if (!isAuthReady) return;
    fetchUsers();
  }, [isAuthReady, pagination.page, pagination.perPage, debouncedKey]);

  useEffect(() => {
    setPageInput(pagination.page);
  }, [pagination.page]);

  const handleDeleteClick = (user) => {
    if (!canDelete) {
      setSnackbar({ open: true, message: "You do not have permission to delete users", color: "error" });
      return;
    }

    setDeleteDialog({ open: true, user, loading: false });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token missing");

      const res = await fetch(`${API_URL}/users/export?${buildUsersQueryString(false)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await readJsonSafely(res);
        throw new Error(apiErrorMessage(data, "Export failed"));
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Export failed", color: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({ open: true, message: "You do not have permission to confirm delete", color: "error" });
      return;
    }

    try {
      setDeleteDialog((prev) => ({ ...prev, loading: true }));

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token missing");

      const res = await fetch(`${API_URL}/users/${deleteDialog.user?.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await readJsonSafely(res);

      if (!res.ok) throw new Error(apiErrorMessage(data, "Delete failed"));

      setSnackbar({
        open: true,
        message: apiSuccessMessage(data, "User deleted successfully"),
        color: "success",
      });

      if (users.length === 1 && pagination.page > 1) {
        setPagination((p) => ({ ...p, page: p.page - 1 }));
      } else {
        await fetchUsers();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Failed to delete user", color: "error" });
    } finally {
      setDeleteDialog({ open: false, user: null, loading: false });
    }
  };

  const handleDeleteCancel = () => {
    if (!canCancelDelete) return;
    setDeleteDialog({ open: false, user: null, loading: false });
  };

  const baseColumns = [
    {
      Header: "ID",
      accessor: "id",
      align: "left",
      width: "5%",
      permission: "users.fields.id.view",
    },
    {
      Header: "Name",
      accessor: "name",
      align: "left",
      width: "12%",
      permission: "users.fields.name.view",
    },
    {
      Header: "Email",
      accessor: "email",
      align: "left",
      width: "18%",
      permission: "users.fields.email.view",
    },
    {
      Header: "Branch",
      accessor: "branch",
      align: "left",
      width: "10%",
      permission: "users.fields.branch.view",
    },
    {
      Header: "Sub Branch",
      accessor: "subbranch",
      align: "left",
      width: "10%",
      permission: "users.fields.sub_branch.view",
    },
    {
      Header: "Emp Code",
      accessor: "user_code",
      align: "left",
      width: "10%",
      permission: "users.fields.user_code.view",
    },
    {
      Header: "Shift",
      accessor: "shift",
      align: "left",
      width: "10%",
      permission: "users.fields.shift.view",
    },
    // {
    //   Header: "Shift Code",
    //   accessor: "shift_code",
    //   align: "left",
    //   width: "10%",
    //   permission: `${PERMISSION_PREFIX}shift_code`,
    // },
    {
      Header: "Shift Time",
      accessor: "shift_time",
      align: "left",
      width: "12%",
      permission: "users.fields.shift.view",
    },
    {
      Header: "Role",
      accessor: "role",
      align: "left",
      width: "10%",
      permission: "users.fields.role.view",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "left",
      width: "8%",
      permission: "users.fields.status.view",
    },
    {
      Header: "Action",
      accessor: "action",
      align: "center",
      width: "12%",
      permission: "users.fields.actions.view",
    },
  ];

  const allowedColumns = baseColumns.filter((column) => hasUsersPermission(column.permission));

  const allRows = useMemo(
    () =>
      users.map((user) => {
        const shift = user.shift || {};
        return {
          id: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.id}
            </SoftTypography>
          ),
          name: (
            <SoftTypography variant="button" fontWeight="medium">
              {`${user.name || ""} ${user.last_name || ""}`.trim() || "-"}
            </SoftTypography>
          ),
          email: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.email}
            </SoftTypography>
          ),
          branch: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.branch?.name || "-"}
            </SoftTypography>
          ),
          subbranch: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.sub_branch?.name || user.subbranch?.name || "-"}
            </SoftTypography>
          ),
          user_code: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.user_code || "-"}
            </SoftTypography>
          ),

          shift: (
            <SoftTypography variant="button" fontWeight="medium">
              {shift.type || "-"}
            </SoftTypography>
          ),
          // shift_code: (
          //   <SoftTypography variant="button" fontWeight="medium">
          //     {shift.shift_code || "-"}
          //   </SoftTypography>
          // ),
          shift_time: (
            <SoftTypography variant="button" fontWeight="medium">
              {shift.start_time && shift.end_time ? `${shift.start_time} - ${shift.end_time}` : "-"}
            </SoftTypography>
          ),
          role: (
            <SoftTypography variant="button" fontWeight="medium">
              {user.custom_role || user.role || "-"}
            </SoftTypography>
          ),
          // status: (
          //   <SoftTypography
          //     variant="button"
          //     fontWeight="medium"
          //     color={user.deleted_at ? "error" : "success"}
          //   >
          //     {user.deleted_at ? "Inactive" : "Active"}
          //   </SoftTypography>
          // ),

          status: (
            <SoftTypography
              variant="button"
              fontWeight="medium"
              sx={{
                color:
                  user.deleted_at
                    ? "#d32f2f"
                    : user.status === "selected"
                    ? "#2e7d32" // green
                    : user.status === "rejected"
                      ? "#d32f2f" // red
                      : user.status === "pending"
                        ? "#ed6c02" // orange
                        : user.status === "shortlisted"
                          ? "#d81b60" // pink
                          : user.status === "training"
                            ? "#6a1b9a" // violet
                            : "#6c757d", // default gray
                textTransform: "capitalize",
              }}
            >
              {user.deleted_at ? "inactive" : user.status || "-"}
            </SoftTypography>
          ),

          action: (
            <SoftBox display="flex" justifyContent="center">
              {canOpenEdit && (
              <Tooltip title="Edit User">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/users/edit/${user.id}`)}
                  aria-label="edit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              )}

              {canManagePermissionOverrides
                && user?.id
                && String(user.id) !== currentUserId && (
                <Tooltip title="User-specific permissions">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => navigate(`/users/permissions/${user.id}`, {
                      state: { fromUsersList: true },
                    })}
                    aria-label="Manage user-specific permissions"
                  >
                    <AdminPanelSettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canDelete && (
                <Tooltip title="Delete User">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(user)}
                    aria-label="delete"
                    disabled={deleteDialog.loading && deleteDialog.user?.id === user.id}
                  >
                    {deleteDialog.loading && deleteDialog.user?.id === user.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </SoftBox>
          ),
        };
      }),
    [
      canDelete,
      canManagePermissionOverrides,
      canOpenEdit,
      currentUserId,
      deleteDialog,
      navigate,
      users,
    ]
  );

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.perPage));
  const entriesStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.perPage + 1;
  const entriesEnd = Math.min(pagination.page * pagination.perPage, pagination.total);

  const handlePageChange = (newPage) => {
    const pageNumber = Number(newPage);
    if (!Number.isFinite(pageNumber)) {
      setPageInput(pagination.page);
      return;
    }

    const nextPage = Math.min(Math.max(pageNumber, 1), totalPages);
    setPagination((p) => (p.page === nextPage ? p : { ...p, page: nextPage }));
    setPageInput(nextPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    if (totalPages > 6) {
      return (
        <SoftBox width="5rem" mx={1}>
          <SoftInput
            inputProps={{ type: "number", min: 1, max: totalPages }}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePageChange(pageInput)}
            onBlur={() => handlePageChange(pageInput)}
          />
        </SoftBox>
      );
    }

    return Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
      <SoftPagination
        item
        key={page}
        active={pagination.page === page}
        onClick={() => handlePageChange(page)}
      >
        {page}
      </SoftPagination>
    ));
  };

  if (!isAuthReady) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2">Loading...</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="medium">
            User List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view users.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="User"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <Dialog open={deleteDialog.open} onClose={!deleteDialog.loading ? handleDeleteCancel : null}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user{" "}
            <strong>
              {`${deleteDialog.user?.name || ""} ${deleteDialog.user?.last_name || ""}`.trim() || "this user"}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {canConfirmDelete && (
          <SoftButton
            onClick={handleDeleteConfirm}
            color="info"
            variant="contained"
            disabled={deleteDialog.loading}
            endIcon={deleteDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleteDialog.loading ? "Processing..." : "Confirm"}
          </SoftButton>
          )}
          {canCancelDelete && (
          <SoftButton
            variant="contained"
            color="error"
            onClick={handleDeleteCancel}
            disabled={deleteDialog.loading}
          >
            Cancel
          </SoftButton>
          )}
        </DialogActions>
      </Dialog>


      <SoftBox p={3}>
        <SoftBox mb={3}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <SoftTypography variant="h4" fontWeight="bold">
              User List
            </SoftTypography>
            <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/users/add")}
              >
                Add New User
              </SoftButton>
              )}
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>download</Icon>}
                onClick={handleExport}
              >
                Export
              </SoftButton>
              {canOpenTrashed && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>delete</Icon>}
                onClick={() => navigate("/users/trashed")}
              >
                Trashed User List
              </SoftButton>
              )}
            </SoftBox>
          </SoftBox>

          {users.length === 0 ? (
            <SoftBox textAlign="center" py={4}>
              <SoftTypography variant="body2">No users found</SoftTypography>
            </SoftBox>
          ) : allowedColumns.length === 0 ? (
            <SoftBox textAlign="center" py={4}>
              <SoftTypography variant="body2">
                Sorry! You don&apos;t have permission.
              </SoftTypography>
            </SoftBox>
          ) : (
            ""
          )}
          <Card style={{ flex: 1, borderRadius: "0px" }}>
            {/* Dropdown Fields */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                padding: "15px",
                marginBottom: "10px",
                flexWrap: "wrap",
              }}
            >
              {canUseOwnerFilter && (
                <div style={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Owner
                  </Typography>
                  <select
                    value={formData.owner}
                    onChange={(event) => handleSelectChange("owner")({ value: event.target.value })}
                    style={{ minHeight: 38, width: "100%", border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
                  >
                    <option value="">All Owners</option>
                    <option value="admin">Admin</option>
                    <option value="vendor">Internal Vendor</option>
                  </select>
                </div>
              )}

              {/* Role */}
              {canViewField("role") && (
                <div style={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Role
                  </Typography>

                  <SearchableSelectLazy
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="roles"
                    options={dropdowns.roles}
                    onFocus={async () => {
                      if (dropdowns.roles.length === 0) {
                        const rolesRes = await fetchWithAuth(`${API_URL}/users/options/roles`);
                        setDropdowns((prev) => ({
                          ...prev,
                          roles: Array.isArray(rolesRes?.roles)
                            ? [
                                { label: "All Roles", value: "" },
                                ...rolesRes.roles.map((r) => ({
                                  label: r.name,
                                  value: r.id,
                                  user_scope: r.user_scope,
                                  scopes: r.scopes || [],
                                })),
                              ]
                            : [],
                        }));
                      }
                    }}
                    isMulti={false}
                    fontSize={"10px"}
                    onChange={handleSelectChange("role_id")}
                    value={dropdowns.roles.find((opt) => opt.value === formData.role_id) || ""}
                  />
                </div>
              )}

              {/* Branch */}
              {canViewField("branch") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Branch
                </Typography>
                <SearchableSelectLazy
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="branch_id"
                  options={dropdowns.branches}
                  onFocus={async () => {
                    if (dropdowns.branches.length === 0) {
                      const branchesRes = await fetchWithAuth(`${API_URL}/users/options/branches`);
                      setDropdowns((prev) => ({
                        ...prev,
                        branches: Array.isArray(branchesRes?.data)
                          ? branchesRes.data.map((b) => ({ label: b.name, value: b.id }))
                          : [],
                      }));
                    }
                  }}
                  isMulti={false}
                  fontSize={"10px"}
                  onChange={handleSelectChange("branch_id")}
                  value={dropdowns.branches.find((opt) => opt.value === formData.branch_id) || ""}
                />
              </div>
              )}

              {/* Sub Branch */}
              {canViewField("sub_branch") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Sub Branch
                </Typography>
                <SearchableSelectLazy
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="subbranch_id"
                  options={dropdowns.subBranches}
                  onFocus={async () => {
                    if (formData.branch_id && dropdowns.subBranches.length === 0) {
                      const subBranchesRes = await fetchWithAuth(`${API_URL}/users/options/sub-branches/${formData.branch_id}`);
                      setDropdowns((prev) => ({
                        ...prev,
                        subBranches: Array.isArray(subBranchesRes?.data)
                          ? subBranchesRes.data.map((sb) => ({ label: sb.name, value: sb.id }))
                          : Array.isArray(subBranchesRes?.subbranches) ? subBranchesRes.subbranches.map((sb) => ({ label: sb.name, value: sb.id })) : [],
                      }));
                    }
                  }}
                  isMulti={false}
                  fontSize={"10px"}
                  onChange={handleSelectChange("subbranch_id")}
                  value={dropdowns.subBranches.find((opt) => opt.value === formData.subbranch_id) || ""}
                />
              </div>
              )}

              {/* Shift */}
              {canViewField("shift") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Shift
                </Typography>

                {loading.branch ? (
                  <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                    Loading shifts...
                  </Typography>
                ) : (
                  <SearchableSelectLazy
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="shift_id"
                    options={dropdowns.branchShifts}
                    isMulti={false}
                    fontSize={"10px"}
                    onChange={handleSelectChange("shift_id")}
                    value={
                      dropdowns.branchShifts.find((opt) => opt.value === formData.shift_id) || ""
                    }
                  />
                )}
              </div>
              )}

              {/* Department */}
              {canViewField("department") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Department
                </Typography>

                <SearchableSelectLazy
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="department_id"
                  options={dropdowns.department}
                  onFocus={async () => {
                    if (dropdowns.department.length === 0) {
                      const res = await fetchWithAuth(`${API_URL}/users/options/departments`);
                      setDropdowns((prev) => ({
                        ...prev,
                        department: Array.isArray(res?.data)
                          ? res.data.map((d) => ({ label: d.name, value: d.id }))
                          : [],
                      }));
                    }
                  }}
                  isMulti={false}
                  fontSize={"10px"}
                  onChange={handleSelectChange("department_id")}
                  value={
                    dropdowns.department.find((opt) => opt.value === formData.department_id) || ""
                  }
                />
              </div>
              )}

              {/* Designation */}
              {canViewField("designation") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Designation
                </Typography>

                <SearchableSelectLazy
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="designation_id"
                  options={dropdowns.designation}
                  onFocus={async () => {
                    if (dropdowns.designation.length === 0) {
                      const res = await fetchWithAuth(`${API_URL}/users/options/designations`);
                      setDropdowns((prev) => ({
                        ...prev,
                        designation: Array.isArray(res?.data)
                          ? res.data.map((d) => ({ label: d.name, value: d.id }))
                          : [],
                      }));
                    }
                  }}
                  isMulti={false}
                  fontSize={"10px"}
                  onChange={handleSelectChange("designation_id")}
                  value={
                    dropdowns.designation.find((opt) => opt.value === formData.designation_id) || ""
                  }
                />
              </div>
              )}

              {canViewField("name") && (
              <div style={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom style={{ fontSize: "12px" }}>
                  Search
                </Typography>
                <SoftInput
                  variant="outlined"
                  size="small"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Enter name..."
                  sx={{ borderRadius: "0px", minHeight: "38px" }}
                />
              </div>
              )}
            </div>

            {allowedColumns.length > 0 ? (
              <DataTable
                table={{
                  columns: allowedColumns,
                  rows: allRows,
                }}
                entriesPerPage={false}
              />
            ) : null}
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              p={3}
            >
              <SoftBox color="text" fontSize="0.875rem">
                Showing {entriesStart} to {entriesEnd} of {pagination.total} entries
              </SoftBox>

              {totalPages > 1 && (
                <SoftPagination variant="gradient" color="info">
                  {pagination.page > 1 && (
                    <SoftPagination item onClick={() => handlePageChange(pagination.page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </SoftPagination>
                  )}
                  {renderPagination()}
                  {pagination.page < totalPages && (
                    <SoftPagination item onClick={() => handlePageChange(pagination.page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </SoftPagination>
                  )}
                </SoftPagination>
              )}
            </SoftBox>
          </Card>
        </SoftBox>


      </SoftBox>
    </DashboardLayout>
  );
};

export default UsersList;
